import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { CalendarDays, Lock, Eye, EyeOff, AlertCircle, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { apiRequest } from "../lib/utils";

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");
  
  const [novaSenha, setNovaSenha] = useState("");
  const [confirmarSenha, setConfirmarSenha] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState("");

  // Verificar token ao carregar
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setError("Token não fornecido. Solicite uma nova recuperação de senha.");
        setVerifying(false);
        return;
      }

      try {
        const response = await apiRequest(`/auth/verify-reset-token/${token}`);
        setTokenValid(true);
        setUserEmail(response.email);
      } catch (err) {
        setError(err.message || "Token inválido ou expirado.");
      } finally {
        setVerifying(false);
      }
    };

    verifyToken();
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validacoes
    if (novaSenha.length < 6) {
      setError("A senha deve ter pelo menos 6 caracteres");
      return;
    }

    if (novaSenha !== confirmarSenha) {
      setError("As senhas não coincidem");
      return;
    }

    setLoading(true);

    try {
      await apiRequest("/auth/reset-password", {
        method: "POST",
        body: JSON.stringify({ token, nova_senha: novaSenha }),
      });
      
      setSuccess(true);
      
      // Redirecionar para login após 3 segundos
      setTimeout(() => {
        navigate("/login");
      }, 3000);
    } catch (err) {
      setError(err.message || "Erro ao redefinir senha.");
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (verifying) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Verificando token...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex">
      {/* Form Section */}
      <div className="flex-1 flex items-center justify-center p-8 bg-white">
        <div className="w-full max-w-md">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center">
              <CalendarDays className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
                Conforme a Resolução
              </h1>
              <p className="text-sm text-slate-500">Gestão Documental</p>
            </div>
          </div>

          <Card className="border-0 shadow-none">
            <CardHeader className="px-0">
              <CardTitle className="text-xl" style={{ fontFamily: 'Manrope' }}>
                Redefinir Senha
              </CardTitle>
              <CardDescription>
                {tokenValid ? `Crie uma nova senha para ${userEmail}` : "Verifique o link de recuperação"}
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0">
              {success ? (
                <div className="space-y-4">
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-800">
                      Senha alterada com sucesso! Redirecionando para o login...
                    </AlertDescription>
                  </Alert>
                  
                  <Link to="/login">
                    <Button className="w-full bg-teal-600 hover:bg-teal-700 mt-4">
                      Ir para Login
                    </Button>
                  </Link>
                </div>
              ) : !tokenValid ? (
                <div className="space-y-4">
                  <Alert variant="destructive" data-testid="reset-error">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                  
                  <Link to="/esqueci-senha">
                    <Button className="w-full bg-teal-600 hover:bg-teal-700">
                      Solicitar Nova Recuperação
                    </Button>
                  </Link>
                  
                  <Link to="/login">
                    <Button variant="ghost" className="w-full">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Voltar para Login
                    </Button>
                  </Link>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  {error && (
                    <Alert variant="destructive" data-testid="reset-form-error">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{error}</AlertDescription>
                    </Alert>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="novaSenha">Nova Senha</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="novaSenha"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={novaSenha}
                        onChange={(e) => setNovaSenha(e.target.value)}
                        className="pl-10 pr-10"
                        required
                        minLength={6}
                        data-testid="reset-new-password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">Mínimo de 6 caracteres</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="confirmarSenha">Confirmar Nova Senha</Label>
                    <div className="relative">
                      <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                      <Input
                        id="confirmarSenha"
                        type={showPassword ? "text" : "password"}
                        placeholder="••••••••"
                        value={confirmarSenha}
                        onChange={(e) => setConfirmarSenha(e.target.value)}
                        className="pl-10"
                        required
                        data-testid="reset-confirm-password"
                      />
                    </div>
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-teal-600 hover:bg-teal-700"
                    disabled={loading}
                    data-testid="reset-submit"
                  >
                    {loading ? "Salvando..." : "Redefinir Senha"}
                  </Button>

                  <Link to="/login">
                    <Button variant="ghost" className="w-full mt-2">
                      <ArrowLeft className="w-4 h-4 mr-2" />
                      Voltar para Login
                    </Button>
                  </Link>
                </form>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Hero Section */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-teal-600 to-teal-700 items-center justify-center p-12">
        <div className="max-w-lg text-white">
          <h2 className="text-4xl font-bold mb-6" style={{ fontFamily: 'Manrope' }}>
            Quase la!
          </h2>
          <p className="text-teal-100 text-lg mb-8">
            Crie uma nova senha segura para proteger sua conta e seus dados.
          </p>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
            <h3 className="font-semibold mb-3">Dicas para uma senha segura:</h3>
            <ul className="space-y-2 text-teal-100">
              <li>• Use pelo menos 6 caracteres</li>
              <li>• Combine letras, números e simbolos</li>
              <li>• Evite informações pessoais obvias</li>
              <li>• Não reutilize senhas antigas</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
